// 偵測區域 (DetectionZones ROI) 純邏輯 — APP#45 / common!96 (capdict v1.8.0)。
// wire 格式 (common/04 §4.1，對標 lsappv3 slot 169 motion_area_config)：
//   { "num": 1, "region0": { "x": 0, "y": 0, "xlen": 100, "ylen": 100 } }
// x/y = 矩形左上角、xlen/ylen = 寬/高，皆為「畫面寬/高的百分比整數 0–100」(解析度無關)。
// 約束 x+xlen ≤ 100、y+ylen ≤ 100；原點左上、x→右、y→下。語意 = include ROI。
// v1 parity 只支援 num:1 單區域 (region0)。UI 拖矩形 + presets → normalize → op:set rawVal。
// SwiftUI/Compose 端 1:1 鏡像此邏輯 (DetectionZones.swift / DetectionZones.kt)。
import type { SignalFrame } from '../../../lib/ws';

/** 單一矩形區域 (百分比整數 0–100)。 */
export interface ZoneRegion {
  x: number;
  y: number;
  xlen: number;
  ylen: number;
}

/** 全畫面預設 (ZoneEnabled=off 的等效偵測範圍)。 */
export const FULL_FRAME: ZoneRegion = { x: 0, y: 0, xlen: 100, ylen: 100 };

/**
 * 最小區域邊長 (百分比)。lsappv3 `_mzMinSize=40` 定義於韌體原生分析網格 (非 0–100 百分比)，
 * 且 spec 明載「韌體不必強制」。client 只用一個小的反退化下限，避免送出 0 面積 / 極小誤觸區域。
 */
export const ZONE_MIN_DIM = 5;

const clampInt = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, Math.round(v)));

/**
 * 把任意矩形夾到合法範圍：整數化、0–100、且 x+xlen ≤ 100 / y+ylen ≤ 100，並套用最小邊長。
 * 先夾 x/y 到 [0,100]，再夾邊長使右/下界不超過 100，最後保證邊長 ≥ ZONE_MIN_DIM (必要時左/上推回)。
 */
export function clampRegion(r: ZoneRegion): ZoneRegion {
  let x = clampInt(r.x, 0, 100);
  let y = clampInt(r.y, 0, 100);
  let xlen = clampInt(r.xlen, ZONE_MIN_DIM, 100);
  let ylen = clampInt(r.ylen, ZONE_MIN_DIM, 100);
  // 溢出處理:優先「縮短邊長」讓右/下界落回 100 (保留使用者拉的起點);
  // 只有當剩餘空間放不下最小邊長時,才把起點往回推。
  if (x + xlen > 100) {
    if (100 - x >= ZONE_MIN_DIM) xlen = 100 - x;
    else x = 100 - xlen;
  }
  if (y + ylen > 100) {
    if (100 - y >= ZONE_MIN_DIM) ylen = 100 - y;
    else y = 100 - ylen;
  }
  return { x, y, xlen, ylen };
}

/**
 * 由拖曳的兩個角 (皆為 0–1 的正規化比例，相對預覽框) 算出區域。任一序皆可 (自動取 min/max)。
 * 回傳已 clampRegion 的合法區域。
 */
export function rectFromDrag(ax: number, ay: number, bx: number, by: number): ZoneRegion {
  const x0 = Math.min(ax, bx);
  const y0 = Math.min(ay, by);
  const x1 = Math.max(ax, bx);
  const y1 = Math.max(ay, by);
  return clampRegion({
    x: x0 * 100,
    y: y0 * 100,
    xlen: (x1 - x0) * 100,
    ylen: (y1 - y0) * 100,
  });
}

/** DetectionZones op:set rawVal payload (v1 parity：num:1 + region0)。 */
export function encodeZones(region: ZoneRegion): { num: number; region0: ZoneRegion } {
  return { num: 1, region0: clampRegion(region) };
}

// ── CAM-60 多區域 (num≤8) ─────────────────────────────────────────────
// 韌體 (device/athena2 motion.c) 支援 num≤8 個矩形 OR 聯集；MAX_ZONES 對齊其 MOTION_MAX_ZONES=8。
// ⚠️ 三端 (kt/swift/ts) 同一份演算法 + golden 向量；改這裡務必同步 DetectionZones.kt / DetectionZones.swift。

/** 韌體支援的最多矩形數 (athena2 MOTION_MAX_ZONES)。回傳數 > 此值時 UI 應擋存。 */
export const MAX_ZONES = 8;

/** op:set rawVal payload (num:K + region0..region(K-1))。空 → num:0 (＝全幀偵測)。 */
export function encodeZoneList(regions: ZoneRegion[]): Record<string, unknown> {
  const m: Record<string, unknown> = { num: regions.length };
  regions.forEach((reg, i) => {
    m[`region${i}`] = clampRegion(reg);
  });
  return m;
}

/**
 * grid (rows×cols 布林) → 最少數量的軸對齊矩形。貪婪法：逐列水平連續段 + 垂直同段合併。
 * 空選 → []。依 (y,x) 排序，三端一致。cells[r][c]，r∈0..rows-1、c∈0..cols-1。
 */
export function mergeGridToRegions(cells: boolean[][], cols: number, rows: number): ZoneRegion[] {
  if (cols <= 0 || rows <= 0 || cells.length < rows) return [];
  const runsOf = (r: number): [number, number][] => {
    const out: [number, number][] = [];
    const row = cells[r];
    let c = 0;
    while (c < cols) {
      if (c < row.length && row[c]) {
        let e = c;
        while (e + 1 < cols && e + 1 < row.length && row[e + 1]) e++;
        out.push([c, e]);
        c = e + 1;
      } else c++;
    }
    return out;
  };
  const rects: ZoneRegion[] = [];
  const emit = (run: [number, number], r0: number, r1: number): void => {
    const x = Math.round((run[0] * 100) / cols);
    const x2 = Math.round(((run[1] + 1) * 100) / cols);
    const y = Math.round((r0 * 100) / rows);
    const y2 = Math.round(((r1 + 1) * 100) / rows);
    rects.push({ x, y, xlen: x2 - x, ylen: y2 - y });
  };
  let open: { key: string; run: [number, number]; start: number }[] = [];
  for (let r = 0; r < rows; r++) {
    const runs = runsOf(r);
    const runKeys = new Set(runs.map((rn) => `${rn[0]},${rn[1]}`));
    const next: { key: string; run: [number, number]; start: number }[] = [];
    for (const o of open) {
      if (runKeys.has(o.key)) next.push(o);
      else emit(o.run, o.start, r - 1);
    }
    const nextKeys = new Set(next.map((o) => o.key));
    for (const run of runs) {
      const k = `${run[0]},${run[1]}`;
      if (!nextKeys.has(k)) next.push({ key: k, run, start: r });
    }
    open = next;
  }
  for (const o of open) emit(o.run, o.start, rows - 1);
  return rects.sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
}

/** 從回報解析出多個 region (region0..region(num-1))；bare region → 單元素；空/"0"/num:0 → []。 */
export function parseZoneList(frame: SignalFrame): ZoneRegion[] {
  const f = frame as Record<string, unknown>;
  const payload = (frame.payload ?? {}) as Record<string, unknown>;
  const candidates: unknown[] = [f.rawVal, payload.rawVal, f.strVal, payload.strVal, f.value, payload.value];
  for (const c of candidates) {
    const obj = coerceRegionContainer(c);
    if (!obj) continue;
    const num = typeof obj.num === 'number' ? obj.num : typeof obj.num === 'string' ? Number(obj.num) : undefined;
    if (num !== undefined && Number.isFinite(num)) {
      if (num === 0) return [];
      const list: ZoneRegion[] = [];
      for (let i = 0; i < num; i++) {
        const d = obj[`region${i}`];
        if (isValidRegion(d)) list.push(d as ZoneRegion);
      }
      if (list.length > 0) return list;
    } else if (isValidRegion(obj)) {
      return [obj as ZoneRegion];
    }
  }
  return [];
}

/** 一個物件是否為合法 region (四個 0–100 整數欄，且右/下界不超界)。 */
function isValidRegion(o: unknown): o is ZoneRegion {
  if (!o || typeof o !== 'object') return false;
  const r = o as Record<string, unknown>;
  const nums = [r.x, r.y, r.xlen, r.ylen];
  if (!nums.every((n) => typeof n === 'number' && Number.isFinite(n))) return false;
  const { x, y, xlen, ylen } = r as unknown as ZoneRegion;
  if (x < 0 || y < 0 || xlen <= 0 || ylen <= 0) return false;
  if (x > 100 || y > 100 || xlen > 100 || ylen > 100) return false;
  if (x + xlen > 100 || y + ylen > 100) return false;
  return true;
}

/**
 * 從 op:get / 屬性變更回報 frame 解析出 region0，優雅退回。
 * 來源順序：frame.rawVal → payload.rawVal → strVal(JSON 字串) → payload。
 * ⚠️ 已知舊機 (lsappv3 2504/2602) slot 169 讀回恆為 `"0"` / 空 → 回 null (未設定)，不當有效區域、不 crash。
 * 回 null = 「未設定 / 讀不到」，UI 顯示未設定並可回退全畫面繪製。
 */
export function parseZoneReport(frame: SignalFrame): ZoneRegion | null {
  const f = frame as Record<string, unknown>;
  const payload = (frame.payload ?? {}) as Record<string, unknown>;
  const candidates: unknown[] = [f.rawVal, payload.rawVal, f.strVal, payload.strVal, f.value, payload.value];
  for (const c of candidates) {
    const obj = coerceRegionContainer(c);
    if (!obj) continue;
    const region = obj.region0 ?? obj; // 可能直接是 region，或 {num,region0}
    if (isValidRegion(region)) return region as ZoneRegion;
  }
  return null;
}

/** 把候選值 (物件 / JSON 字串 / 韌體 bug 的 "0") 轉成一個可能含 region0 的物件；失敗回 null。 */
function coerceRegionContainer(c: unknown): Record<string, unknown> | null {
  if (c == null) return null;
  if (typeof c === 'object') return c as Record<string, unknown>;
  if (typeof c === 'string') {
    const s = c.trim();
    if (s === '' || s === '0') return null; // 韌體未持久化的退化回讀
    try {
      const parsed = JSON.parse(s);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** 常用區域快選 preset (8 個)。id 供 i18n / testTag，region 為百分比整數。 */
export interface ZonePreset {
  id: string;
  region: ZoneRegion;
}
export const ZONE_PRESETS: ZonePreset[] = [
  { id: 'full', region: { x: 0, y: 0, xlen: 100, ylen: 100 } },
  { id: 'top', region: { x: 0, y: 0, xlen: 100, ylen: 50 } },
  { id: 'bottom', region: { x: 0, y: 50, xlen: 100, ylen: 50 } },
  { id: 'left', region: { x: 0, y: 0, xlen: 50, ylen: 100 } },
  { id: 'right', region: { x: 50, y: 0, xlen: 50, ylen: 100 } },
  { id: 'center', region: { x: 25, y: 25, xlen: 50, ylen: 50 } },
  { id: 'topStrip', region: { x: 0, y: 0, xlen: 100, ylen: 30 } },
  { id: 'bottomStrip', region: { x: 0, y: 70, xlen: 100, ylen: 30 } },
];
